export type CalculatorConfigType = {
    buildHash: string;
    cdnPrefix: string;
    embedCdnPrefix: string;
    calculator: {
        definition: {
            fullName: string;
            slug: string;
            customJs: string;
            help: string;
            equations: {
                f: {
                    [key: string]: string;
                };
                name: string;
                locked: string[];
            }[];
            variables: string[];
            args: {
                [key: string]: ArgsTypeWithCustomKeys;
            };
            conditions: CalculatorErrorConditonType[];

            unitSwitchers: {
                [key: string]: {
                    name: string;
                    slug: string;
                    ratios: {
                        [key: string]: string;
                    };
                    fullName: string;
                    subunits: string[];
                    availability: string;
                    reversedName: string;
                    reversedFullName: string;
                    in: string;
                    out: string;
                }[];
            };
            valueSelects: {
                [key: string]: {
                    name: string;
                    value: string;
                    ekaSelectId?: string;
                }[];
            };
            equationKey?: string;
            equationMapping?: {
                [key: string]: string;
            };
        };
        text: {
            title: string;
        };
        tests: [
            {
                steps: {
                    command: string;
                    variable: string;
                    value: string;
                }[];
            },
        ];
        ekaDescription: string;
        ekaHowToUse: string;
        ekaInterpretation?: EkaInterpretation;
    };
    sentryEnvironment: string;
};

export type CalculatorErrorConditonType = {
    f: string;
    name: string;
    variables: string[];
};

export type ArgType = {
    name: string;
    label: string;
    placeholder: string;
    monetary: boolean;
    suffix: string;
    suffixEnd: string;
    default: string;
    defaultImperial: string;
    advanced: boolean;
    locked: boolean;
    lockedPermanently: boolean;
    autosaved: boolean;
    help: string;
    textRowBefore: string;
    dontFormat: boolean;
    roundTo: number;
    fieldType: string;
    unitSwitcherId: string;
    unitSwitcherReversed: boolean;
    unitDefault: string;
    unitDefaultImperial: string;
    baseUnit: string;
    availableUnits: string;
    unitRecalculateMode: boolean;
    valueSelectId: string;
    ekaHidden: boolean;
    ekaAfterVariable: string;
    ekaDefaultValue: string;
    useRoundedDefaultValue?: boolean;
};

export type ArgsTypeWithCustomKeys = ArgType & {
    inputValue?: string;
    ekaAutoFillSectionId?: string;
};

export interface EkaInterpretation {
    type: 'column' | 'html';
    info: string;
    columnsToInclude: { title: string; name: string }[];
    data: { [key: string]: string }[];
}

export enum CALCULATORS_NAMES {
    HOMA_IR = 'homa-ir',
    WAIST_HIP_RATIO = 'waist-hip-ratio',
    QUICKI = 'quicki',
    CHILD_PUGH = 'child-pugh',
    MELD = 'meld',
    GFR = 'gfr',
    NAFLD = 'nafld',
    ESS = 'ess',
    ABCD_TIA = 'abcd-tia',
    LMP_EDD = 'lmp-edd',
    US_EDD = 'us-edd',
    CVD_RISK = 'cvd-risk',
    HAS_BLED = 'has-bled',
    CHA2DS2_VASC = 'cha2ds2-vasc',
    RCRI = 'rcri',
    VITAL_CAPACITY = 'vital-capacity',
    BODE_INDEX = 'bode-index',
    FIB_4 = 'fib-4',
    IV_FLOW_RATE = 'iv-flow-rate',
    PEDIATRIC_DOSE = 'pediatric-dose',
    COPD_CAT = 'copd-cat',
    CCI = 'cci',
    REVEAL_TEST = 'reveal-test',
    IPSS = 'ipss',
    CRCL = 'crcl',
    PGE2 = 'pge2',
    GESTATIONAL_AGE = 'gestational-age',
    DASI = 'dasi',
    GCS = 'gcs',
    STOP_BANG = 'stop-bang',
}

export enum CALCULATOR_RESULTS_NAME {
    HOMA_IR = 'HOMA-IR',
    WAIST_HIP_RATIO = 'Waist Hip Ratio',
    QUICKI = 'QUICKI',
    CHILD_PUGH = 'Child Pugh',
    MELD = 'MELD Score',
    GFR = 'eGFR',
    NAFLD = 'NAFLD fibrosis score',
    ESS = 'Epworth sleepiness score',
    ABCD_TIA = 'ABCD² Score',
    LMP_EDD = 'LMP EDD',
    US_EDD = 'USG EDD',
    CVD_RISK = 'CVD 10 Yr Risk',
    HAS_BLED = 'HAS-BLED score',
    CHA2DS2_VASC = 'CHA2DS2-Vasc score',
    RCRI = 'RCRI',
    VITAL_CAPACITY = 'Vital Capacity',
    BODE_INDEX = 'Bode Index Score',
    FIB_4 = 'FIB-4',
    IV_FLOW_RATE = 'IV Flow Rate',
    PEDIATRIC_DOSE = 'Pediatric Dose',
    COPD_CAT = 'COPD - CAT',
    CCI = 'CCI',
    REVEAL_TEST = 'REVEAL Test',
    IPSS = 'International Prostate Symptom Score (IPSS)',
    CRCL = 'Creatinine Clearance (CrCl)',
    PGE2 = 'Ratio (Pg/E2 or P/E2)',
    GESTATIONAL_AGE = 'Gestational Age',
    DASI = 'Duke Activity Status Index',
    GCS = 'Glasgow Coma Scale',
    STOP_BANG = 'STOP-BANG',
}

export enum GROWTH_CHART_NAME {
    HEIGHT_FOR_AGE = 'Height for Age',
    WEIGHT_FOR_AGE = 'Weight for Age',
    OFC_FOR_AGE = 'OFC for Age',
    WEIGHT_FOR_HEIGHT = 'Weight for Height',
    BMI_FOR_AGE = 'BMI for Age',
}

export enum CALCULATOR_VIEW {
    VITALS = 'vitals',
    INJECTIONS = 'injections',
    MEDICATIONS = 'medications',
}
